import clsx from 'clsx'
import { useEffect, useState, type ReactNode } from 'react'

import type { Event } from '@/services/api/schemas/events'
import {
  EVENT_TYPE_COLOR_VAR,
  formatCameraName,
  formatDurationSeconds,
  getAbsenceInterval,
  getEventMediaUrl,
  getEventSubtype,
  getEventTitle,
  getGuardIncidentInterval,
} from '@/shared/lib/eventTypes'
import { formatDate, formatFrameStamp, formatTimeSec } from '@/shared/lib/format'
import { CameraFrame, Lightbox, StateBlock } from '@/shared/ui'

import s from './EventDetail.module.css'

interface FactRow {
  label: string
  value: ReactNode
  big?: boolean
}

function FactCard({ title, rows }: { title: string; rows: FactRow[] }) {
  return (
    <div className={s.fact}>
      <div className={s.factTitle}>{title}</div>
      {rows.map((row) => (
        <div key={row.label} className={s.row}>
          <span>{row.label}</span>
          <b className={clsx(row.big && s.big)}>{row.value}</b>
        </div>
      ))}
    </div>
  )
}

function typeFacts(event: Event): { title: string; rows: FactRow[] } | null {
  switch (event.type) {
    case 'cashier_missing': {
      const { start, end } = getAbsenceInterval(event)
      return {
        title: 'Отсутствие на рабочем месте',
        rows: [
          {
            label: 'Касса',
            value: event.cash_desk_number !== undefined ? `Касса №${event.cash_desk_number}` : '—',
          },
          { label: 'Начало', value: start, big: true },
          { label: 'Окончание', value: end ?? '—', big: true },
          {
            label: 'Продолжительность',
            value:
              event.absence_seconds !== undefined
                ? formatDurationSeconds(event.absence_seconds)
                : 'продолжается',
          },
        ],
      }
    }
    case 'security_overstay': {
      const { start, end } = getGuardIncidentInterval(event)
      return {
        title: 'Длительное сидение на посту',
        rows: [
          {
            label: 'Зона',
            value: event.guard_zone_number !== undefined ? `Зона №${event.guard_zone_number}` : '—',
          },
          { label: 'Начало', value: start, big: true },
          { label: 'Окончание', value: end ?? '—', big: true },
          {
            label: 'Продолжительность',
            value:
              event.guard_incident_seconds !== undefined
                ? formatDurationSeconds(event.guard_incident_seconds)
                : '—',
          },
        ],
      }
    }
    case 'smoke_fire':
      return { title: 'Тип', rows: [{ label: 'Уточнение', value: getEventSubtype(event) ?? '—' }] }
    case 'conflict':
      // По требованию заказчика карточка деталей для конфликта не показывается.
      return null
    case 'unknown':
      return event.label ? { title: 'Тип', rows: [{ label: 'Подпись', value: event.label }] } : null
  }
}

export function EventDetail({ event }: { event: Event | undefined }) {
  const [playing, setPlaying] = useState(false)
  const [lightboxOpen, setLightboxOpen] = useState(false)
  // При переходе к другому событию возвращаемся к снимку и закрываем лайтбокс.
  useEffect(() => {
    setPlaying(false)
    setLightboxOpen(false)
  }, [event?.id])
  if (!event)
    return (
      <StateBlock kind="empty" message="Выберите событие из списка, чтобы посмотреть подробности" />
    )

  const color = EVENT_TYPE_COLOR_VAR[event.type]
  const facts = typeFacts(event)
  const snapshotUrl = getEventMediaUrl(event, 'image')
  const videoUrl = getEventMediaUrl(event, 'video')
  const camera = formatCameraName(event.camera_id)
  const stamp = formatFrameStamp(event.detected_at)
  // Во весь экран открываем только снимок событий без видео: у остальных есть клип.
  const canEnlarge = Boolean(snapshotUrl) && !videoUrl
  // Снимок охраны — квадратный коллаж 3×3, в кадре 16:9 он обрезается. Если есть клип,
  // вместо коллажа показываем его первый кадр.
  const previewVideoUrl = event.type === 'security_overstay' ? videoUrl : undefined

  return (
    <div className={s.detail}>
      <div>
        <span className={s.type} style={{ color }}>
          {getEventTitle(event)}
        </span>
        <h2 className={s.externalId}>{event.id}</h2>
      </div>

      <CameraFrame
        stamp={stamp}
        camera={camera}
        snapshotUrl={snapshotUrl}
        previewVideoUrl={previewVideoUrl}
        onSnapshotClick={canEnlarge ? () => setLightboxOpen(true) : undefined}
        // Без медиа кадр показывает текст «Видео или фото события недоступно» (ТЗ п. 7.3);
        // рамку детекции не рисуем: её координат у нас нет.
        playable={Boolean(videoUrl)}
        onPlay={() => setPlaying(true)}
        videoSrc={playing ? videoUrl : undefined}
      />
      {lightboxOpen && canEnlarge && snapshotUrl && (
        <Lightbox
          src={snapshotUrl}
          caption={`${stamp} · ${camera}`}
          onClose={() => setLightboxOpen(false)}
        />
      )}

      <div className={s.facts}>
        {facts && <FactCard title={facts.title} rows={facts.rows} />}
        <FactCard
          title="Источник"
          rows={[
            { label: 'Камера', value: <span title={event.camera_id}>{camera}</span> },
            { label: 'Дата', value: formatDate(event.detected_at) },
            { label: 'Время', value: formatTimeSec(event.detected_at) },
          ]}
        />
      </div>
    </div>
  )
}
